import {
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Leaf,
  LucideIcon,
  Package,
  SlidersHorizontal
} from "lucide-react";
import { ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "../../../lib/utils";
import { MappingProductSelect } from "./components/MappingProductSelect";
import { MAPPING_HEADER_COPY } from "./constants/mappingHeader.constants";
import { ClimateImpactState } from "./types/mappingHeader.types";

interface MappingHeaderCardProps {
  productName?: string;
  salesUnitName?: string;
  options: DropdownOption[];
  selectedValue: string;
  onSelect: (value: string) => void;
  versionLabel: string | null;
  climateImpact: ClimateImpactState;
  productSummaryPath: string | null;
  checkUseeRoleUser?: boolean | null;
}

const IconTile = ({ icon: Icon }: { icon: LucideIcon }) => (
  <span className="tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary/10">
    <Icon className="tw-h-3.5 tw-w-3.5 tw-text-primary" />
  </span>
);

const Segment = ({
  icon,
  label,
  className,
  children
}: {
  icon: LucideIcon;
  label: string;
  className?: string;
  children: ReactNode;
}) => (
  <div className={cn("tw-flex tw-min-w-0 tw-items-center tw-gap-2", className)}>
    <IconTile icon={icon} />
    <div className="tw-flex tw-min-w-0 tw-flex-col">
      <span className="tw-text-[10px] tw-leading-none tw-text-primary/70">{label}</span>
      {children}
    </div>
  </div>
);

const HeaderIconButton = ({
  icon: Icon,
  title,
  onClick
}: {
  icon: LucideIcon;
  title: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    className="tw-flex tw-h-5 tw-w-5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded tw-transition hover:tw-bg-white/50"
  >
    <Icon className="tw-h-3.5 tw-w-3.5 tw-text-primary" />
  </button>
);

export const MappingHeaderCard = ({
  productName,
  salesUnitName,
  options,
  selectedValue,
  onSelect,
  versionLabel,
  climateImpact,
  productSummaryPath,
  checkUseeRoleUser
}: MappingHeaderCardProps) => {
  const [metricsExpanded, setMetricsExpanded] = useState(true);
  const [detailsVisible, setDetailsVisible] = useState(true);

  return (
    <div className="tw-absolute tw-right-6 tw-top-4 tw-z-[99]">
      <div
        className={cn(
          "tw-flex tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-primary tw-bg-card tw-shadow-sm tw-transition-all",
          metricsExpanded ? (detailsVisible ? "tw-w-[640px]" : "tw-w-[280px]") : "tw-w-auto"
        )}
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-bg-primary/10 tw-px-3 tw-py-1.5">
          {metricsExpanded ? (
            <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
              <SlidersHorizontal className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-primary" />
              <span className="tw-truncate tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-primary">
                {MAPPING_HEADER_COPY.cardLabel}
              </span>
            </div>
          ) : (
            <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
              <Package className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-primary" />
              <span className="tw-max-w-[160px] tw-truncate tw-text-sm tw-font-semibold tw-text-primary">
                {productName || MAPPING_HEADER_COPY.productFallbackLabel}
              </span>
            </div>
          )}

          <div className="tw-ml-auto tw-flex tw-items-center tw-gap-1">
            {metricsExpanded ? (
              <HeaderIconButton
                icon={detailsVisible ? Eye : EyeOff}
                title={
                  detailsVisible ? MAPPING_HEADER_COPY.hideDetails : MAPPING_HEADER_COPY.showDetails
                }
                onClick={() => setDetailsVisible(visible => !visible)}
              />
            ) : null}
            <HeaderIconButton
              icon={metricsExpanded ? ChevronUp : ChevronDown}
              title={metricsExpanded ? MAPPING_HEADER_COPY.collapse : MAPPING_HEADER_COPY.expand}
              onClick={() => setMetricsExpanded(expanded => !expanded)}
            />
          </div>
        </div>

        {metricsExpanded ? (
          <div className="tw-flex tw-items-stretch tw-gap-3 tw-px-3 tw-py-2">
            <Segment icon={Package} label={MAPPING_HEADER_COPY.productLabel} className="tw-flex-1">
              <MappingProductSelect
                productName={productName}
                options={options}
                selectedValue={selectedValue}
                onSelect={onSelect}
              />
              {salesUnitName ? (
                <span className="tw-truncate tw-text-[10px] tw-leading-none tw-text-primary/70">
                  {salesUnitName}
                </span>
              ) : null}
            </Segment>

            {detailsVisible ? (
              <>
                {versionLabel ? (
                  <Segment
                    icon={SlidersHorizontal}
                    label={MAPPING_HEADER_COPY.versionLabel}
                    className="tw-w-[120px] tw-border-l tw-border-primary/15 tw-pl-3"
                  >
                    <span className="tw-truncate tw-text-sm tw-font-semibold tw-leading-tight tw-text-primary">
                      {versionLabel}
                    </span>
                  </Segment>
                ) : null}

                <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-2 tw-border-l tw-border-primary/15 tw-pl-3">
                  <IconTile icon={Leaf} />
                  {climateImpact.status === "ready" && productSummaryPath ? (
                    !checkUseeRoleUser ? (
                      <Link
                        to={productSummaryPath}
                        title={MAPPING_HEADER_COPY.viewSummary}
                        className="tw-group tw--mx-1 tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 tw-py-1.5 tw-transition hover:tw-bg-muted/30"
                      >
                        <span className="tw-flex tw-min-w-0 tw-flex-col">
                          <span className="tw-text-[10px] tw-leading-none tw-text-primary/70">
                            {MAPPING_HEADER_COPY.climateImpactLabel}
                          </span>
                          <span className="tw-truncate tw-text-sm tw-font-semibold tw-leading-tight tw-text-primary">
                            {climateImpact.measure.toFixed(2)}{" "}
                            <span className="tw-text-[10px] tw-font-normal tw-opacity-70">
                              {climateImpact.unit}
                            </span>
                          </span>
                        </span>
                        <ArrowUpRight className="tw-ml-auto tw-h-4 tw-w-4 tw-shrink-0 tw-text-primary tw-opacity-60 tw-transition group-hover:tw-opacity-100" />
                      </Link>
                    ) : (
                      <span className="tw--mx-1 tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 tw-py-1.5  tw-cursor-not-allowed">
                        <span className="tw-flex tw-min-w-0 tw-flex-col">
                          <span className="tw-text-[10px] tw-leading-none tw-text-primary/70">
                            {MAPPING_HEADER_COPY.climateImpactLabel}
                          </span>
                          <span className="tw-text-[10px] tw-font-normal tw-opacity-70">
                            Calculated: Upgrade to see results
                          </span>
                        </span>
                      </span>
                    )
                  ) : (
                    <div
                      className="tw-flex tw-min-w-0 tw-flex-col"
                      title={MAPPING_HEADER_COPY.pendingTooltip}
                    >
                      <span className="tw-text-[10px] tw-leading-none tw-text-primary/70">
                        {MAPPING_HEADER_COPY.climateImpactLabel}
                      </span>
                      <span className="tw-truncate tw-text-sm tw-font-semibold tw-leading-tight tw-text-muted-foreground">
                        {MAPPING_HEADER_COPY.pending}
                      </span>
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};
